import React from 'react'
import _ from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import { colors } from '@lmc/common/constants'
import { ChartLogList } from '../../../../components/generic'
import withToolbar from '../withToolbar'
import Highcharts from './Highcharts'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import chartTitles from '../chartTitles'
import utc from 'dayjs/plugin/utc'
import { useGetFluidIntaketargets } from '../hooks/useGetFluidIntakeTargets'
import styled from 'styled-components'
import FluidsDisplay from 'screens/Residents/screens/ResidentChartsDashboardScreen/components/FluidsDisplay'
import { useParams } from 'react-router-dom'

dayjs.extend(utc)

interface Log {
  deleted?: boolean
  timeLogged: string | Date
  measurements?: {
    fluids_in?: {
      value?: number
    }
    fluids_out?: {
      value?: number
    }
    [key: string]:
      | {
          value?: number
        }
      | undefined
  }
  categoryColor?: string
}

interface FluidIntakeDate {
  date: string
  intakeTarget: number | null
}

interface FluidsChartProps {
  from?: Dayjs | Date
  logs?: Log[]
  to?: Dayjs | Date
  mock?: boolean
}

interface SeriesData {
  name: string
  stack?: string
  color: string
  data: any[]
  type?: string
  step?: string | boolean
  zIndex?: number
  showInLegend?: boolean
  enableMouseTracking?: boolean
  marker?: {
    enabled?: boolean
    radius?: number
    color?: string
    fillColor?: string
    lineColor?: string
    symbol?: string
  }
}

const FluidsChart: React.FC<FluidsChartProps> = ({ logs, to, from, mock }) => {
  const { id: residentId } = useParams<{ id: string }>()

  const { fluidIntakeData } = useGetFluidIntaketargets({
    residents: residentId || '',
    to,
    from,
    fluidTrackingFlagEnabled: !!residentId,
    mock,
  })
  const fluidIntakeDates = fluidIntakeData?.[0]?.dates

  const getSeriesData = (data: Log[], type: string): [number, number][] => {
    return _.chain(data)
      .filter((d) => !d.deleted)
      .groupBy((log) =>
        dayjs(log.timeLogged).utc().startOf('day').add(1, 'h').format(),
      )
      .map((value, date) => {
        const total = _.chain(value)
          .map(`measurements.${type}.value`)
          .filter(_.isNumber) // triggers error for non numeric values
          .sum()
          .value()

        return [Date.parse(dayjs(date).toString()), total] as [number, number]
      })
      .value()
  }

  const getIntakeTargetData = (intakeDates: FluidIntakeDate[]): any[] => {
    const dataPoints = _.chain(intakeDates)
      .map((item) => {
        const [year, month, day] = item.date.split('-').map(Number)
        const date = dayjs()
          .year(year)
          .month(month - 1)
          .date(day)
          .startOf('day')
        return { x: date.valueOf(), y: item.intakeTarget, date: item.date }
      })
      .value()

    const segments: any[][] = []
    let currentSegment: any[] = []

    dataPoints.forEach((point) => {
      if (point.y !== null) {
        currentSegment.push(point)
      } else {
        if (currentSegment.length > 0) {
          segments.push([...currentSegment])
          currentSegment = []
        }
      }
    })

    if (currentSegment.length > 0) {
      segments.push(currentSegment)
    }

    return dataPoints.map((point, index) => {
      if (point.y === null) {
        return [point.x, null]
      }

      const previousPoint = index > 0 ? dataPoints[index - 1] : null
      const valueChanged =
        !previousPoint ||
        previousPoint.y === null ||
        point.y !== previousPoint.y

      if (valueChanged) {
        return [point.x, point.y]
      }

      return {
        x: point.x,
        y: point.y,
        marker: { enabled: false },
      }
    })
  }

  if (!logs || !logs.length) {
    return (
      <EmptyState
        title="Shoot... No data to show you"
        description="No logs to display"
        icon={<NoDataIcon />}
      />
    )
  }

  const chartColors = [colors.purple, colors.green]
  const fluidsInLogs = _.filter(logs, (log: Log) =>
    _.get(log, 'measurements.fluids_in.value'),
  ) as Log[]
  const fluidsOutLogs = _.filter(logs, (log: Log) =>
    _.get(log, 'measurements.fluids_out.value'),
  ) as Log[]

  const series: SeriesData[] = []
  if (fluidsInLogs && fluidsInLogs.length) {
    series.push({
      name: 'Fluids in',
      stack: 'fluids_in',
      color: fluidsInLogs[0].categoryColor || chartColors[0],
      data: getSeriesData(fluidsInLogs, 'fluids_in'),
    })
  }

  if (fluidsOutLogs && fluidsOutLogs.length) {
    series.push({
      name: 'Fluids out',
      stack: 'fluids_out',
      color: fluidsOutLogs[0].categoryColor || chartColors[1],
      data: getSeriesData(fluidsOutLogs, 'fluids_out'),
    })
  }

  if (fluidIntakeDates && fluidIntakeDates.length) {
    const intakeTargetData = getIntakeTargetData(fluidIntakeDates)
    if (intakeTargetData.length > 0) {
      series.push({
        name: '',
        type: 'line',
        step: 'left',
        color: '#ff6699',
        data: intakeTargetData,
        zIndex: 10,
        showInLegend: false,
        enableMouseTracking: false,
        marker: {
          enabled: true,
          radius: 2,
          fillColor: '#ff6699',
          lineColor: '#ff6699',
          symbol: 'circle',
        },
      })
    }
  }

  const config = {
    title: chartTitles.fluids,
    yAxisLabel: 'Fluids In / Out (ml)',
    legendEnabled: true,
    to,
    from,
    chartType: 'column',
    series,
    plotOptions: {
      turboThreshold: 10000,
    },
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        {series.length ? <Highcharts config={config} /> : null}
        <FluidsDisplayContainer>
          <FluidsDisplay
            residentId={residentId || ''}
            flag="fluid_tracking_chart_with_intake_target"
            displayInactiveState
            displayIcon
            mock={mock}
          />
        </FluidsDisplayContainer>
      </div>
      <ChartLogList logs={logs} />
    </div>
  )
}

const FluidsDisplayContainer = styled.div`
  position: absolute;
  bottom: 24px;
  left: 24px;
`

export default withToolbar(FluidsChart, {
  pdfExport: {
    title: 'Fluids Chart',
  },
  timeWindow: 7,
})
